import CloseIcon from "@mui/icons-material/Close";
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Modal } from "app/components/Modal";
import React from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { valueForOnlyStringInputTextFields } from "utils/helpers";
import { actions } from "../../redux/slice";
import {
  selectFormData,
  selectIsSaving,
  selectIsAddModalOpen,
} from "../../redux/selector";
import { adminTemplateForm } from "../../types";
import { useStyles } from "./styles";

type FieldConfig = {
  key: keyof adminTemplateForm;
  label: string;
};

type RowConfig = FieldConfig[];

const ROWS: RowConfig[] = [
  [
    { key: "documentName",        label: "TEMPLATE_NAME"        },
    { key: "templateCode",        label: "TEMPLATE_CODE"        },
  ],
  [
    { key: "templateDescription", label: "TEMPLATE_DESCRIPTION" },
  ],
  [
    { key: "version",             label: "VERSION"              },
    { key: "format",              label: "FORMAT"               },
  ],
];

const REQUIRED_FIELDS: Array<keyof adminTemplateForm> = ["documentName", "templateCode", "format"];

const ADMIN_TEMPLATE_MODAL = "ADMIN_TEMPLATE.MODAL";

const AddTemplateModal = () => {
  const { t, i18n } = useTranslation();
  const styles = useStyles();
  const dispatch = useDispatch();

  const form = useSelector(selectFormData)!;
  const isSaving = useSelector(selectIsSaving);
  const isOpen = useSelector(selectIsAddModalOpen);

  const onClose = () => {
    if (isSaving) return;
    dispatch(actions.resetForm());
    dispatch(actions.closeAddModal());
  };

  const setField = (name: keyof adminTemplateForm, value: string) =>
    dispatch(
      actions.setFormField({
        name,
        value: valueForOnlyStringInputTextFields(value),
      }),
    );

  const isValid = REQUIRED_FIELDS.every((key) => Boolean(form[key]?.trim()));

  const onSubmit = () => {
    if (!isValid || isSaving) return;
    dispatch(
      actions.createDocument({
        ...form,
        documentPath: "silagi/assets/2026-07-08/ABCD/template.pdf",
        mimeType: "",
        documentType: "",
        documentSize: "",
      }),
    );
  };

  return (
    <Modal open={isOpen} onClose={onClose}>
      <Box className={styles.modal} sx={{ direction: i18n.dir() }}>
        <Box className={styles.header}>
          <Typography className={styles.title}>
            {t(`${ADMIN_TEMPLATE_MODAL}.ADD_TITLE`)}
          </Typography>
          <IconButton
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t("BUTTONS.CANCEL")}
            disabled={isSaving}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        <Box className={styles.body}>
          {ROWS.map((row, rowIdx) => (
            <Box key={rowIdx} className={row.length > 1 ? styles.row : ""}>
              {row.map(({ key, label }) => (
                <Box key={key} className={styles.field}>
                  <Typography className={styles.label}>
                    {t(`${ADMIN_TEMPLATE_MODAL}.FIELDS.${label}`)}
                  </Typography>
                  <TextField
                    className={
                      key === "templateDescription"
                        ? styles.multilineInput
                        : styles.input
                    }
                    fullWidth
                    multiline={key === "templateDescription"}
                    variant="outlined"
                    placeholder={t(`${ADMIN_TEMPLATE_MODAL}.FIELDS.${label}`)}
                    value={form[key]}
                    disabled={isSaving}
                    InputProps={{ notched: false }}
                    onChange={(e) => setField(key, e.target.value)}
                  />
                </Box>
              ))}
            </Box>
          ))}
        </Box>

        <Box className={styles.footer}>
          <Button
            className={styles.cancelButton}
            variant="outlined"
            onClick={onClose}
            disabled={isSaving}
          >
            {t("BUTTONS.CANCEL")}
          </Button>
          <Button
            className={styles.addButton}
            variant="contained"
            disableElevation
            disabled={!isValid || isSaving}
            onClick={onSubmit}
          >
            {t("BUTTONS.ADD")}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddTemplateModal;
